import { recordCrash, recordPageView, recordTrace, recordUse } from '../api/websiteHost';
import { Recorder, SeverityLevel } from '../recorder';

const SkipDebugTracesInTestingOnly: boolean = true;

interface BrowserRecorderOptions {
  skipDebugTracesInTestingOnly: boolean;
}

export abstract class BrowserRecorder implements Recorder {
  private readonly skipDebugTracesInTestingOnly: boolean = SkipDebugTracesInTestingOnly;

  protected constructor(options?: BrowserRecorderOptions) {
    this.skipDebugTracesInTestingOnly = options ? options.skipDebugTracesInTestingOnly : SkipDebugTracesInTestingOnly;
  }

  crash(error: Error, message?: string): void {
    if (window.isTestingOnly) {
      console.error(error, `LoopCar: Crash: ${message}: ${error.message}, ${error.stack}`);
    }
    recordCrash({
      body: {
        message: `LoopCar: Crash: ${message}: ${error.message}, ${error.stack}`
      }
    });
  }

  trace(message: string, severityLevel: SeverityLevel, args?: any): void {
    if (window.isTestingOnly) {
      if (severityLevel === SeverityLevel.Error) {
        if (args) {
          console.error(`LoopCar: TraceError: ${message}`, args);
        } else {
          console.error(`LoopCar: TraceError: ${message}`);
        }
      } else if (severityLevel === SeverityLevel.Warning) {
        if (args) {
          console.warn(`LoopCar: TraceWarning: ${message}`, args);
        } else {
          console.warn(`LoopCar: TraceWarning: ${message}`);
        }
      } else if (severityLevel === SeverityLevel.Information) {
        if (args) {
          console.info(`LoopCar: TraceInformation: ${message}`, args);
        } else {
          console.info(`LoopCar: TraceInformation: ${message}`);
        }
      } else {
        if (args) {
          console.log(`LoopCar: TraceDebug: ${message}`, args);
        } else {
          console.log(`LoopCar: TraceDebug: ${message}`);
        }
      }

      if (severityLevel === SeverityLevel.Debug && this.skipDebugTracesInTestingOnly) {
        return;
      }
    }

    recordTrace({
      body: {
        arguments: args ?? {},
        level: severityLevel.toString(),
        messageTemplate: message
      }
    });
  }

  traceDebug(message: string, args?: any): void {
    this.trace(message, SeverityLevel.Debug, args);
  }

  traceInformation(message: string, args?: any): void {
    this.trace(message, SeverityLevel.Information, args);
  }

  trackPageView(path: string): void {
    recordPageView({
      body: {
        path
      }
    });

    if (window.isTestingOnly) {
      console.log(`LoopCar: PageView: ${path}`);
    }
  }

  trackUsage(eventName: string, additional: { [val: string]: any } | undefined): void {
    recordUse({
      body: {
        eventName,
        additional
      }
    });

    if (window.isTestingOnly) {
      console.log(`LoopCar: Track:${eventName}, with: ${additional}`);
    }
  }
}
